import api from 'api';
import {useGetApi} from 'common/hooks/useGetApi';
import {SwitchTransferListDialog} from 'components/dialogs/SwitchTransferListDialog';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {IDiplomatObject} from 'types/DiplomatObject';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import * as yup from 'common/yup';
import {IDiplomatAdminUser} from 'types/DiplomatAdminUser';
import {unpackId} from 'common/diplomatIdUtil';

const formNames = {userId: 'userId', roleId: 'roleId'} as const;

const validationSchema = yup.object({
   [formNames.userId]: yup.array().of(yup.string()),
   [formNames.roleId]: yup.array().of(yup.string()),
});

const getUsersOptions = {
   transformData(data: IDiplomatObject[]) {
      data.forEach((item) => (item.name = item.name || (item as IDiplomatAdminUser).userName));
      return data;
   },
};

type Props = {
   permissionEntries: IDiplomatObject[];
   onClose: (users?: IDiplomatObject[]) => void;
};
export const AddPermissionEntryDialog = ({permissionEntries, onClose}: Props) => {
   const {t} = useTranslation();
   const {request: getRoles, state: adminroles = []} = useGetApi<IDiplomatObject[]>(api.adminroles);
   const {request: getUsers, state: adminusers = []} = useGetApi<IDiplomatObject[]>(
      api.adminUsers,
      getUsersOptions
   );

   React.useEffect(getRoles, [getRoles]);
   React.useEffect(getUsers, [getUsers]);

   const values = useMemo(() => {
      const vals = [[], []] as [string[], string[]];
      permissionEntries.forEach((p) => {
         if (p.objectType === DiplomatObjectType.enum.ADMIN_ROLE) {
            vals[0].push(p.id);
         } else {
            vals[1].push(p.id);
         }
      });
      return vals;
   }, [permissionEntries, permissionEntries.length]);

   const onAdd = React.useCallback(
      (nameObj?: {
         [formNames.userId]: string[];
         [formNames.roleId]: string[];
         _switch: boolean;
      }) => {
         if (!nameObj) {
            onClose();
            return;
         }
         const {userId, roleId, _switch} = nameObj;

         const selected = [] as IDiplomatObject[];

         userId.forEach((uId) => {
            const existed = values[1].indexOf(uId);
            if (existed >= 0) return;

            const user = adminusers.find((user) => user.id === uId);
            user && selected.push(user);
         });
         roleId.forEach((uId) => {
            const existed = values[0].indexOf(uId);
            if (existed >= 0) return;

            const user = adminroles.find((user) => user.id === uId);
            user && selected.push(user);
         });

         onClose(selected);
      },
      [onClose, adminusers, adminroles, values]
   );

   // console.debug('perms', permissionEntries, values, adminroles, adminusers);
   return (
      <SwitchTransferListDialog
         data={{
            title: t('Add Permission Entry'),
            validationSchema: validationSchema,
            names: [formNames.roleId, formNames.userId],
            labels: [t('Admin Roles'), t('Admin Users')],
            options: [adminroles, adminusers],
            values,
         }}
         onClose={onAdd}
      />
   );
};
